import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { api, ApiError } from '../api';
import { t, tDyn } from '../i18n';

function PasswordForm() {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [repeat, setRepeat] = useState('');
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (next !== repeat) return setMsg({ ok: false, text: t('account.mismatch') });
    try {
      await api.changePassword(current, next);
      setCurrent('');
      setNext('');
      setRepeat('');
      setMsg({ ok: true, text: t('account.changed') });
    } catch (err) {
      setMsg({ ok: false, text: err instanceof ApiError ? err.message : String(err) });
    }
  };

  return (
    <form className="card auth-wide" onSubmit={submit}>
      <h2>{t('account.password')}</h2>
      <label>
        {t('account.current')}
        <input type="password" autoComplete="current-password" value={current} onChange={(e) => setCurrent(e.target.value)} required />
      </label>
      <label>
        {t('account.new')}
        <input type="password" autoComplete="new-password" minLength={10} value={next} onChange={(e) => setNext(e.target.value)} required />
        <span className="muted small">{t('auth.passwordHint')}</span>
      </label>
      <label>
        {t('account.repeat')}
        <input type="password" autoComplete="new-password" value={repeat} onChange={(e) => setRepeat(e.target.value)} required />
      </label>
      <button className="primary">{t('common.save')}</button>
      {msg && <div className={msg.ok ? 'success' : 'error'}>{msg.text}</div>}
    </form>
  );
}

function UsersAdmin({ myId }: { myId: string }) {
  const qc = useQueryClient();
  const users = useQuery({ queryKey: ['users'], queryFn: api.users });
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const run = async (fn: () => Promise<unknown>, ok?: string) => {
    try {
      await fn();
      setMsg(ok ? { ok: true, text: ok } : null);
    } catch (err) {
      setMsg({ ok: false, text: err instanceof ApiError ? err.message : String(err) });
    }
    void qc.invalidateQueries({ queryKey: ['users'] });
  };

  return (
    <section className="card">
      <h2>{t('account.users')}</h2>
      <table className="table">
        <thead>
          <tr>
            <th>{t('common.name')}</th>
            <th>{t('account.campaigns')}</th>
            <th>{t('account.admin')}</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {users.data?.map((u) => (
            <tr key={u.id}>
              <td>{u.name}</td>
              <td className="small">
                {u.campaigns.length === 0
                  ? t('common.none')
                  : u.campaigns.map((c, i) => (
                      <span key={c.id}>
                        {i > 0 && ', '}
                        <Link to={`/c/${c.id}`}>{c.name}</Link> <span className="muted">({tDyn(`role.${c.role}`)})</span>
                      </span>
                    ))}
              </td>
              <td>
                <input
                  type="checkbox"
                  checked={u.isAdmin}
                  disabled={u.id === myId}
                  aria-label={t('account.admin')}
                  onChange={(e) => void run(() => api.updateUser(u.id, { isAdmin: e.target.checked }))}
                />
              </td>
              <td className="num row">
                <button
                  className="small-btn"
                  onClick={() => {
                    const pw = prompt(t('account.resetPrompt', { name: u.name }));
                    if (pw) void run(() => api.updateUser(u.id, { password: pw }), t('account.resetDone', { name: u.name }));
                  }}
                >
                  {t('account.resetPassword')}
                </button>
                {u.id !== myId && (
                  <button
                    className="danger small-btn"
                    onClick={() => confirm(t('account.deleteConfirm', { name: u.name })) && void run(() => api.deleteUser(u.id))}
                  >
                    {t('common.delete')}
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {msg && <div className={msg.ok ? 'success' : 'error'}>{msg.text}</div>}
    </section>
  );
}

export function AccountPage() {
  const auth = useQuery({ queryKey: ['auth'], queryFn: api.authState, staleTime: Infinity });
  const user = auth.data?.user;
  if (!user) return null;
  return (
    <div className="page-narrow">
      <header className="page-head">
        <h1>{t('account.title')}</h1>
        <Link to="/">← {t('campaigns.title')}</Link>
      </header>
      <p className="muted">{t('account.loggedInAs', { name: user.name })}</p>
      <PasswordForm />
      {user.isAdmin && <UsersAdmin myId={user.id} />}
    </div>
  );
}
